import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, tap } from "rxjs";

import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { KeyValuePair, TreeNode } from "@tylertech/tyler-api-types";

import { ApiService } from "./apiService";
import { SecurityService } from "./security.service";
import { StorageService } from "./storageService";
import { UserProfileService } from "./userProfile.service";
import { NavigationService } from "./navigation.service";

export const PIN_NAV_MENU = "pinNavMenu";

export interface MenuItem extends TreeNode
{
    isFavorite?: boolean;

    /* override */ Children?: Array<MenuItem>;
}

export interface ExpandableTreeNode extends TreeNode
{
    $expanded?: boolean;
}

@Injectable()
export class NavMenuService
{
    private readonly log = getLoggerFor("NavMenuService");

    private readonly disabledSubject = new BehaviorSubject<boolean>(false);
    private readonly menuChangeSubject = new BehaviorSubject<ReadonlyArray<MenuItem>>(undefined);
    private readonly toggleSubject: BehaviorSubject<boolean>;

    readonly disabled: Observable<boolean>;
    readonly menuChange: Observable<ReadonlyArray<MenuItem>>;
    readonly toggleChange: Observable<boolean>;

    private favoriteItems: ReadonlyArray<MenuItem>;
    private menuItems: ReadonlyArray<MenuItem>;

    private pinnedState?: boolean;

    /**
     * This is really just for testing. Testing needed a way to wait till fetchMainMenu had finished before starting
     * the `expect` statements.
     */
    private fetchingPromise?: Promise<void>;

    constructor(
        private readonly $apiService: ApiService,
        private readonly $securityService: SecurityService,
        private readonly $storageService: StorageService,
        private readonly userProfileService: UserProfileService,
        private readonly $navigationService: NavigationService
    )
    {
        this.pinnedState = this.$storageService.Get<boolean>(PIN_NAV_MENU) === true;
        this.toggleSubject = new BehaviorSubject<boolean>(this.pinnedState);

        this.disabled = this.disabledSubject.asObservable();
        this.menuChange = this.log.enabled
            ? this.menuChangeSubject.asObservable()
                .pipe(
                    tap(x =>
                    {
                        this.log.debug(`MenuChange!`, { menuItems: x });
                    }))
            : this.menuChangeSubject.asObservable();

        this.toggleChange = this.toggleSubject.asObservable();

        this.$securityService.principleChange
            .subscribe(() =>
            {
                if (this.$securityService.isAuthenticated)
                {
                    this.log.debug(`SecurityService.principleChange! Refetching the main menu.`);

                    this.fetchMainMenu();

                    if (this.IsNavPinned)
                    {
                        this.toggle(true);
                    }
                    return;
                }

                this.log.debug(`SecurityService.principleChange! clearing the main menu.`);
                this.menuChangeSubject.next([]);
                this.toggleSubject.next(false);
            });
        this.$navigationService.onResetState().subscribe(() => setTimeout(() => this.updateMenuData(), 100));
    }

    get IsNavPinned(): boolean { return this.pinnedState; }

    private refreshFavorites(): Promise<ReadonlyArray<TreeNode>>
    {
        return this.$apiService.sendAuth<void, ReadonlyArray<TreeNode>>("IHubService/RefreshFavorites");
    }

    private favorites(): Promise<ReadonlyArray<TreeNode>>
    {
        return this.$apiService.sendAuth<void, ReadonlyArray<TreeNode>>("IHubService/Favorites");
    }

    private loadMainMenu(): Promise<ReadonlyArray<TreeNode>>
    {
        return this.$apiService.sendAuth<void, ReadonlyArray<TreeNode>>("IHubService/MainMenu");
    }

    @LogInvocation(LogLevel.DEBUG)
    private fetchMainMenu(): void
    {
        this.fetchingPromise = this.favorites()
            .then((favorites: ReadonlyArray<MenuItem>) =>
            {
                this.favoriteItems = favorites;
                this.favoriteItems?.forEach(
                    x =>
                    {
                        x.isFavorite = true;
                    });

                return this.loadMainMenu();
            })
            .then((menu: ReadonlyArray<TreeNode>) =>
            {
                this.menuItems = menu;

                this.updateMenuData();
            })
            .finally(() =>
            {
                this.fetchingPromise = undefined;
            });
    }

    @LogInvocation(LogLevel.DEBUG)
    private updateFavorites(): Promise<void>
    {
        return this.refreshFavorites()
            .then((favorites: ReadonlyArray<TreeNode>) =>
            {
                this.favoriteItems = favorites;
                this.favoriteItems?.forEach(
                    x =>
                    {
                        x.isFavorite = true;
                    });

                this.updateMenuData();
            });
    }

    protected updateMenuData(): void
    {
        const data = this.favoriteItems && this.favoriteItems[0]?.Children
            ? [
                ...this.favoriteItems,
                ...this.menuItems
            ]
            : [
                ...this.menuItems
            ];

        this.menuChangeSubject.next([...data]);
    }

    addToFavorites(node: TreeNode): Promise<void>
    {
        const link = node.Execute.substring(node.Execute.indexOf("/apps/"));
        const favorite: KeyValuePair<string, string> = {
            Key: node.Key,
            Value: link,
        };
        return this.userProfileService.SaveFavoriteApp(favorite)
            .then(() =>
            {
                return this.updateFavorites();
            });
    }

    removeFromFavorites(node: TreeNode): Promise<void>
    {
        const link = node.Execute.substring(node.Execute.indexOf("/apps/"));
        const favorite: KeyValuePair<string, string> = {
            Key: node.Key,
            Value: link,
        };

        return this.userProfileService.RemoveFavoriteApp(favorite)
            .then(() =>
            {
                return this.updateFavorites();
            });
    }

    setDisabled(value: boolean): void
    {
        this.disabledSubject.next(value);
    }

    setMenuOpen(pinned: boolean): void
    {
        this.$storageService.Set(PIN_NAV_MENU, pinned === true);
        this.pinnedState = pinned;

        if (!pinned)
        {
            this.toggle();
        }
    }

    closeMenu(): void
    {
        this.toggleSubject.next(false);
    }

    @LogInvocation(LogLevel.DEBUG)
    toggle(open: boolean = false): void
    {
        if (!open)
        {
            open = !this.toggleSubject.value;
        }
        if (this.$securityService.isAuthenticated)
        {
            this.toggleSubject.next(open);
        }
    }

    get IsMenuOpen(): boolean
    {
        return this.toggleSubject.value;
    }

    updateNodes(node: ExpandableTreeNode, selectedNode: ExpandableTreeNode): void
    {
        if (this.hasSelectedChild(node, selectedNode))
            node.$expanded = true;
        else
            node.$expanded = undefined;
        if (node.Children)
            node.Children.forEach(c => this.updateNodes(c, selectedNode));
    }

    hasSelectedChild(node: ExpandableTreeNode, selectedNode: ExpandableTreeNode): boolean
    {
        if (node.Children)
            return node.Children.some(c => this.hasSelectedChild(c, selectedNode));
        return node.TreePath === selectedNode.TreePath;
    }
}
